import type { Translations } from '@/i18n/locales/en';
import type { Exception, ExceptionChange, ExceptionValue } from '@restaurant/api-contracts';

export type ExceptionDraftStatus = 'new' | 'updated' | 'removed' | null;

export interface ExceptionDraftRow {
  id: string;
  value: ExceptionValue;
  status: ExceptionDraftStatus;
  before?: ExceptionValue;
}

export interface ExceptionsDraft {
  rows: ExceptionDraftRow[];
  targets: string[];
}

type SummaryCopy = Translations['exceptionsQuickEdit']['summary'];

const valueOf = (exception: Exception): ExceptionValue => ({
  services: exception.services,
  type: exception.type,
  startDate: exception.startDate,
  endDate: exception.endDate,
  shifts: exception.shifts,
  reason: exception.reason,
});

export function buildExceptionsDraft(
  exceptions: Exception[],
  changes: ExceptionChange[],
): ExceptionsDraft {
  const rows = new Map<string, ExceptionDraftRow>(
    exceptions.map((exception) => [
      exception.id,
      { id: exception.id, value: valueOf(exception), status: null },
    ]),
  );
  for (const change of changes) {
    if (change.kind === 'exceptionCreated') {
      rows.set(change.exceptionId, {
        id: change.exceptionId,
        value: {
          services: change.services,
          type: change.type,
          startDate: change.startDate,
          endDate: change.endDate,
          shifts: change.shifts,
          reason: change.reason,
        },
        status: 'new',
      });
      continue;
    }
    if (!rows.has(change.exceptionId)) continue;
    if (change.kind === 'exceptionUpdated') {
      rows.set(change.exceptionId, {
        id: change.exceptionId,
        value: change.after,
        status: 'updated',
        before: change.before,
      });
      continue;
    }
    const row = rows.get(change.exceptionId);
    if (row) rows.set(change.exceptionId, { ...row, status: 'removed' });
  }
  // Soonest first - operators scan upcoming holidays top-down.
  const sorted = [...rows.values()].sort(
    (a, b) => a.value.startDate.localeCompare(b.value.startDate) || a.id.localeCompare(b.id),
  );
  return {
    rows: sorted,
    targets: sorted.flatMap((row) => (row.status === null ? [] : [row.id])),
  };
}

const labelOf = (value: ExceptionValue): string => {
  const range =
    value.startDate === value.endDate ? value.startDate : `${value.startDate} – ${value.endDate}`;
  return value.reason ? `${range} · ${value.reason}` : range;
};

export function exceptionChangeLabels(
  exceptions: Exception[],
  changes: ExceptionChange[],
): string[] {
  const values = new Map(exceptions.map((exception) => [exception.id, valueOf(exception)]));
  return changes.map((change) =>
    labelOf(
      change.kind === 'exceptionCreated'
        ? change
        : change.kind === 'exceptionUpdated'
          ? change.after
          : (values.get(change.exceptionId) ?? change.before),
    ),
  );
}

export function summarizeExceptionsDraft(changes: ExceptionChange[], copy: SummaryCopy): string {
  const created = changes.filter((change) => change.kind === 'exceptionCreated').length;
  const updated = changes.filter((change) => change.kind === 'exceptionUpdated').length;
  const removed = changes.filter((change) => change.kind === 'exceptionRemoved').length;
  return [
    ...(created ? [copy.created(created)] : []),
    ...(updated ? [copy.updated(updated)] : []),
    ...(removed ? [copy.removed(removed)] : []),
  ].join(', ');
}
